import { useState, useEffect, useCallback } from 'react'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'

export interface WorkScopeWithStatus {
  id: number
  workScope: string
  workScopeDisplay: string
  active: boolean
  regionWorkScopeId: number | null
  toolsNeeded: string[]
  suppliesNeeded: string[]
  toolsCount: number
  suppliesCount: number
}

export function useRegionWorkScopes() {
  const { regionId } = useRegionId()
  const [workScopes, setWorkScopes] = useState<WorkScopeWithStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState<Set<number>>(new Set())

  const fetchWorkScopes = useCallback(async () => {
    if (!regionId) {
      setWorkScopes([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const res = await authFetchJson<WorkScopeWithStatus[]>(`/work-scopes/all-with-status?regionId=${regionId}`)
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch work scopes')
      }
      setWorkScopes(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching work scopes:', err)
    } finally {
      setLoading(false)
    }
  }, [regionId])

  const toggleWorkScope = useCallback(async (workScopeId: number, active: boolean) => {
    if (!regionId) {
      return
    }

    setWorkScopes(prev => prev.map(ws =>
      ws.id === workScopeId ? { ...ws, active } : ws
    ))

    setUpdating(prev => new Set(prev).add(workScopeId))

    try {
      const response = await authFetch('/work-scopes/toggle-active', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          regionId,
          workScopeId,
          active,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to update work scope status')
      }

      await fetchWorkScopes()
    } catch (err) {
      setWorkScopes(prev => prev.map(ws =>
        ws.id === workScopeId ? { ...ws, active: !active } : ws
      ))
      setError(err instanceof Error ? err.message : 'Failed to update work scope')
      console.error('Error toggling work scope:', err)
      throw err
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev)
        newSet.delete(workScopeId)
        return newSet
      })
    }
  }, [regionId, fetchWorkScopes])

  useEffect(() => {
    fetchWorkScopes()
  }, [fetchWorkScopes])

  return {
    workScopes,
    loading,
    error,
    toggleWorkScope,
    updating,
    refetch: fetchWorkScopes,
  }
}
